import React, { useState, useEffect, useRef, FormEvent, KeyboardEvent } from 'react';
import { apiClient } from '../../api/client';

interface ScheduleHighlight {
  id: string;
  label: string;
  sortOrder: number;
}

const styles: Record<string, React.CSSProperties> = {
  section: {
    marginBottom: '2rem',
    padding: '1.5rem',
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  sectionTitle: {
    color: '#0f2d1f',
    fontSize: '1.2rem',
    marginBottom: '1rem',
    borderBottom: '2px solid #c9a84c',
    paddingBottom: '0.25rem',
  },
  highlightItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.6rem 0.75rem',
    borderBottom: '1px solid #eee',
    gap: '0.5rem',
  },
  label: {
    flex: 1,
    fontSize: '0.9rem',
    cursor: 'pointer',
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
  },
  editInput: {
    flex: 1,
    padding: '0.35rem 0.5rem',
    border: '1px solid #1a6b3c',
    borderRadius: '4px',
    fontSize: '0.9rem',
    outline: 'none',
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
  },
  controlBtn: {
    padding: '0.25rem 0.5rem',
    backgroundColor: 'transparent',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '0.8rem',
    cursor: 'pointer',
    lineHeight: 1,
  },
  deleteBtn: {
    padding: '0.25rem 0.5rem',
    backgroundColor: '#d32f2f',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.8rem',
    cursor: 'pointer',
    fontWeight: 600,
    lineHeight: 1,
  },
  addForm: {
    marginTop: '1rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  input: {
    padding: '0.5rem 0.75rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '0.9rem',
    flex: 1,
  },
  btn: {
    padding: '0.5rem 1rem',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  error: {
    color: '#d32f2f',
    backgroundColor: '#fdecea',
    padding: '0.5rem 0.75rem',
    borderRadius: '4px',
    marginBottom: '1rem',
    fontSize: '0.85rem',
  },
  emptyState: {
    color: '#666',
    fontStyle: 'italic',
    padding: '0.5rem 0',
  },
};

export function ScheduleHighlightsManager() {
  const [highlights, setHighlights] = useState<ScheduleHighlight[]>([]);
  const [newLabel, setNewLabel] = useState('');
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const editInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchHighlights();
  }, []);

  useEffect(() => {
    if (editingId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingId]);

  async function fetchHighlights() {
    try {
      const data = await apiClient<ScheduleHighlight[]>('/legacy-ball/schedule-highlights');
      setHighlights(data);
    } catch {
      setError('Failed to load schedule highlights.');
    }
  }

  function getErrorMessage(err: unknown): string {
    const apiErr = err as { message?: string | string[] };
    if (Array.isArray(apiErr.message)) {
      return apiErr.message[0];
    }
    return apiErr.message || 'An error occurred.';
  }

  async function handleAdd(e: FormEvent) {
    e.preventDefault();
    if (!newLabel.trim()) return;
    setError('');
    try {
      const created = await apiClient<ScheduleHighlight>('/legacy-ball/schedule-highlights', {
        method: 'POST',
        body: { label: newLabel },
      });
      setHighlights((prev) => [...prev, created]);
      setNewLabel('');
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    }
  }

  async function handleDelete(id: string) {
    setError('');
    try {
      await apiClient(`/legacy-ball/schedule-highlights/${id}`, { method: 'DELETE' });
      setHighlights((prev) => prev.filter((h) => h.id !== id));
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    }
  }

  async function handleMoveUp(index: number) {
    if (index === 0) return;
    setError('');
    const reordered = [...highlights];
    [reordered[index - 1], reordered[index]] = [reordered[index], reordered[index - 1]];
    const ids = reordered.map((h) => h.id);
    try {
      const updated = await apiClient<ScheduleHighlight[]>('/legacy-ball/schedule-highlights/reorder', {
        method: 'PUT',
        body: { ids },
      });
      setHighlights(updated);
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    }
  }

  async function handleMoveDown(index: number) {
    if (index === highlights.length - 1) return;
    setError('');
    const reordered = [...highlights];
    [reordered[index], reordered[index + 1]] = [reordered[index + 1], reordered[index]];
    const ids = reordered.map((h) => h.id);
    try {
      const updated = await apiClient<ScheduleHighlight[]>('/legacy-ball/schedule-highlights/reorder', {
        method: 'PUT',
        body: { ids },
      });
      setHighlights(updated);
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    }
  }

  function startEdit(highlight: ScheduleHighlight) {
    setEditingId(highlight.id);
    setEditValue(highlight.label);
  }

  function cancelEdit() {
    setEditingId(null);
    setEditValue('');
  }

  async function saveEdit() {
    if (!editingId) return;
    setError('');
    try {
      const updated = await apiClient<ScheduleHighlight>(
        `/legacy-ball/schedule-highlights/${editingId}`,
        { method: 'PATCH', body: { label: editValue } },
      );
      setHighlights((prev) =>
        prev.map((h) => (h.id === editingId ? updated : h)),
      );
      setEditingId(null);
      setEditValue('');
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    }
  }

  function handleEditKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveEdit();
    } else if (e.key === 'Escape') {
      cancelEdit();
    }
  }

  return (
    <div style={styles.section}>
      <h2 style={styles.sectionTitle}>Schedule Highlights</h2>

      {error && <div style={styles.error}>{error}</div>}

      {highlights.length === 0 ? (
        <p style={styles.emptyState}>No schedule highlights yet.</p>
      ) : (
        <div>
          {highlights.map((highlight, index) => (
            <div key={highlight.id} style={styles.highlightItem}>
              {editingId === highlight.id ? (
                <input
                  ref={editInputRef}
                  style={styles.editInput}
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={handleEditKeyDown}
                  onBlur={saveEdit}
                />
              ) : (
                <span
                  style={styles.label}
                  onClick={() => startEdit(highlight)}
                  title="Click to edit"
                >
                  {highlight.label}
                </span>
              )}
              <div style={styles.controls}>
                <button
                  style={styles.controlBtn}
                  onClick={() => handleMoveUp(index)}
                  disabled={index === 0}
                  title="Move up"
                >
                  ↑
                </button>
                <button
                  style={styles.controlBtn}
                  onClick={() => handleMoveDown(index)}
                  disabled={index === highlights.length - 1}
                  title="Move down"
                >
                  ↓
                </button>
                <button
                  style={styles.deleteBtn}
                  onClick={() => handleDelete(highlight.id)}
                  title="Delete"
                >
                  ×
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={handleAdd} style={styles.addForm}>
        <input
          style={styles.input}
          placeholder="New highlight label"
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          maxLength={100}
        />
        <button type="submit" style={styles.btn}>Add</button>
      </form>
    </div>
  );
}
